import { z } from 'zod'
import { InstantieId, SectionId } from './id.js'
import { Laag } from './laag.js'
import { Moment, Person } from './moment.js'

/**
 * Het koppelregister van de auteursmap: welke Yjs-client bij welke persoon hoort (besluit van 2 november 2026,
 * eisenboek 12.49, ontwerp 6.9, R-97, R-188, R-154).
 *
 * Yjs zet bij elk stuk tekst de client die het maakte, niet de mens. De auteursmap in het sectiedocument
 * legt die brug, en de pod vult hem uit het token van de verbinding (`apps/runtime/src/authors.ts`). Tot
 * 2 november 2026 stond die koppeling alleen in het document en in het geheugen van de pod, en dat had
 * een gat: na een crash van de pod zonder wegschrijven stuurt een browser die opnieuw verbindt de tekst
 * van anderen mee, en de pod koppelde een client die hij niet meer kende aan wie hem doorstuurde. De tekst
 * van een ander kwam dan op zijn naam.
 *
 * Het register lost dat op. Een regel staat in de opslag **voordat** de pod ook maar één stuk tekst van
 * die client in het levende document aanneemt; tekst die een browser ooit van de pod kreeg, heeft dus
 * altijd een regel hier. Na een herstart houdt een bekende client zijn persoon, wie hem ook doorstuurt,
 * en wordt een client alleen aan de verbinding gekoppeld die hem stuurt als hij werkelijk nieuw is.
 *
 * **Per generatie.** De attributie per zin gaat niet over een naad heen (4.6) en de enige reset van de
 * auteursmap is een nieuwe generatie (valkuil 9); het register volgt die grens.
 *
 * **Twee soorten.** Een client van een persoon (`persoon`), en een client van de pod zelf (`pod`): de
 * pod schrijft onder zijn eigen client de stempels, het zaaien en een terugzetting, en na een crash
 * stuurt een browser die ook mee. Zo'n client heeft geen persoon en wordt nooit aan een verbinding
 * gekoppeld.
 *
 * **Append-only, en de eerste wint.** Een regel wordt nooit gewijzigd. Biedt de pod een koppeling aan voor
 * een client die er al staat, dan krijgt hij de bestaande terug, en die houdt hij aan.
 */
export const KoppelingSoort = z.enum(['persoon', 'pod'])
export type KoppelingSoort = z.infer<typeof KoppelingSoort>

/** Een Yjs-client: een getal van 32 bits zonder teken, zoals Yjs het kiest. */
export const ClientId = z.number().int().min(0).max(0xffff_ffff)
export type ClientId = z.infer<typeof ClientId>

/** Eén koppeling zoals de pod haar aanbiedt. */
export const KoppelingRequest = z
  .strictObject({
    clientId: ClientId,
    soort: KoppelingSoort,
    /** Bij een client van een persoon die persoon, bij een client van de pod niets. */
    persoon: Person.optional(),
  })
  .superRefine((request, ctx) => {
    if ((request.soort === 'persoon') !== (request.persoon !== undefined)) {
      ctx.addIssue({ code: 'custom', path: ['persoon'], message: 'een koppeling van een persoon noemt die persoon; een koppeling van de pod geen' })
    }
  })
export type KoppelingRequest = z.infer<typeof KoppelingRequest>

/** Het grootste aantal koppelingen in één verzoek. Een browser die na een crash opnieuw verbindt, stuurt er hooguit zoveel als de generatie clients heeft. */
export const KOPPELINGEN_PER_VERZOEK = 500

/** Wat de pod in één keer aanbiedt: de nieuwe clients van één wijziging, elk één keer. */
export const KoppelingenRequest = z
  .strictObject({
    koppelingen: z.array(KoppelingRequest).min(1).max(KOPPELINGEN_PER_VERZOEK),
  })
  .superRefine((request, ctx) => {
    const seen = new Set<number>()
    for (const [index, koppeling] of request.koppelingen.entries()) {
      if (seen.has(koppeling.clientId)) ctx.addIssue({ code: 'custom', path: ['koppelingen', index, 'clientId'], message: 'elke client één keer per verzoek' })
      seen.add(koppeling.clientId)
    }
  })
export type KoppelingenRequest = z.infer<typeof KoppelingenRequest>

/** Een regel zoals de opslag hem bewaart en teruggeeft. */
export const Koppeling = z.strictObject({
  instantieId: InstantieId,
  laag: Laag,
  sectieId: SectionId,
  generatie: z.number().int().positive(),
  clientId: ClientId,
  soort: KoppelingSoort,
  persoon: Person.nullable(),
  vastgelegdOp: Moment,
})
export type Koppeling = z.infer<typeof Koppeling>
